import { useEffect } from "react";
import { useLocalSearchParams } from "expo-router";
import { useFleetIncident, useTenantIncident, type IncidentDetail } from "@visioni/a3s-monitoring/app";
import { useReplaceTenantRoute } from "@src/features/tenants";
import { EMPTY, formatInstant } from "../format";
import { activationHref, clientIncidentsHref, fleetClientHref, incidentHref, operatorNotesAroundHref } from "../routes";
import { useActivationScopeOptions } from "../hooks/useActivationScopeOptions";
import { useDirectoryTenantName } from "../hooks/useDirectoryTenantName";
import { IncidentDetailView } from "../components/IncidentDetailView";
import { PatriotToolsPage } from "../components/PatriotToolsPage";
import { QueryNotice } from "../components/QueryNotice";

const rawId = (id: string) => id;

const subtitleOf = (incident: IncidentDetail) =>
  [
    `started ${formatInstant(incident.startedAt)}`,
    `client ${incident.clientNo}`,
    incident.policeCalled ? "police called" : undefined,
  ]
    .filter((part) => part !== undefined)
    .join(" · ");

/**
 * Activations link to the fleet page, which serves a ticket whichever tenant
 * holds it and redirects to that tenant's own page when the directory knows it:
 * an incident's activations can come from cameras a3s does not mark.
 */
const activationLinks = (tenantId: string) => (ticketNo: string) => activationHref(tenantId, ticketNo, undefined);

/** The client links, in the viewer's zone like every time on the page. */
const clientLinks = (tenantId: string, incident: IncidentDetail) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    clientHref: fleetClientHref(tenantId, incident.clientNo),
    clientIncidentsHref: clientIncidentsHref(tenantId, incident.clientNo, { at: incident.startedAt, timeZone }),
    ticketlessCallHref: (call: { readonly at: string }) =>
      operatorNotesAroundHref(tenantId, incident.clientNo, call.at, timeZone),
  };
};

/** One incident from the tenant route, which serves it only when it is counted under this tenant. */
export function TenantIncidentScreen() {
  const { tenantId, incidentId = "" } = useLocalSearchParams<{ tenantId: string; incidentId?: string }>();
  const { incident, isLoading, error, refetch } = useTenantIncident(incidentId);
  const scope = useActivationScopeOptions();
  const tenantName = useDirectoryTenantName();

  return (
    <PatriotToolsPage title="Genuine incident" {...(incident !== undefined && { subtitle: subtitleOf(incident) })}>
      {incident === undefined ?
        <QueryNotice isLoading={isLoading} error={error} onRetry={refetch} />
      : <IncidentDetailView
          incident={incident}
          tenantLabel={tenantName(tenantId) ?? tenantId}
          siteLabel={scope.siteLabel}
          cameraLabel={scope.cameraLabel}
          activationHref={activationLinks(tenantId)}
          {...clientLinks(tenantId, incident)}
        />
      }
    </PatriotToolsPage>
  );
}

/**
 * One incident by id alone, for one counted under a tenant the directory does
 * not hold, or under none. One whose tenant it does hold is redirected there.
 */
export function FleetIncidentScreen() {
  const replaceRoute = useReplaceTenantRoute();
  const { tenantId, incidentId = "" } = useLocalSearchParams<{ tenantId: string; incidentId?: string }>();
  const { incident, isLoading, error, refetch } = useFleetIncident(incidentId);
  const tenantName = useDirectoryTenantName();

  const owner = incident?.a3sTenant;
  const known = owner !== undefined && tenantName(owner) !== undefined ? owner : undefined;

  useEffect(() => {
    if (known !== undefined) replaceRoute(incidentHref(tenantId, incidentId, known));
  }, [known, tenantId, incidentId, replaceRoute]);

  return (
    <PatriotToolsPage title="Genuine incident" {...(incident !== undefined && { subtitle: subtitleOf(incident) })}>
      {incident === undefined || known !== undefined ?
        <QueryNotice isLoading={isLoading || known !== undefined} error={error} onRetry={refetch} />
      : <IncidentDetailView
          incident={incident}
          tenantLabel={owner ?? EMPTY}
          siteLabel={rawId}
          cameraLabel={rawId}
          activationHref={activationLinks(tenantId)}
          {...clientLinks(tenantId, incident)}
        />
      }
    </PatriotToolsPage>
  );
}
